import { useIntl } from "react-intl";
import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import { RoleStatuses } from "~enums/roleStatuses.ts";
import { Roles } from "~enums/roles.ts";
import { useAddRole } from "~hooks/apiHooks/useAddRole.ts";
import { useDeleteRole } from "~hooks/apiHooks/useDeleteRole.ts";
import { useNotification } from "~hooks/useNotification.ts";
import type { RosterDataTableRow } from "~pages/Roster/types/rosterDataTableRow.ts";

type Props = {
  entityId?: string;
  seasonId?: string;
  fixtureId?: string;
  onSuccess: () => void | Promise<void>;
  successMessageIntl: string;
};

type CaptainRow = RosterDataTableRow & DataTableRow;

export const useRosterCaptainToggle = ({ entityId, seasonId, fixtureId, onSuccess, successMessageIntl }: Props) => {
  const { formatMessage } = useIntl();
  const notification = useNotification();

  const handleRoleSuccess = async () => {
    await onSuccess();
    notification.success({ message: formatMessage({ id: successMessageIntl }), placement: "bottomRight" });
  };

  const { mutateAsync: createCaptainRole } = useAddRole({
    onSuccess: handleRoleSuccess,
  });
  const { mutateAsync: deleteCaptainRole } = useDeleteRole({
    onSuccess: handleRoleSuccess,
  });

  const handleCaptainToggle = async (row: CaptainRow, isCaptain: boolean) => {
    row.disabled = true;
    try {
      if (!isCaptain && row.captainRoleId) {
        await deleteCaptainRole(row.captainRoleId);
      } else if (isCaptain && !row.captainRoleId && entityId && seasonId) {
        await createCaptainRole({
          personId: row.personId,
          entityId,
          seasonId,
          role: Roles.Captain,
          fixtureId,
          status: RoleStatuses.Active,
        });
      }
    } finally {
      row.disabled = false;
    }
  };

  return {
    handleCaptainToggle,
    deleteCaptainRole,
  };
};
